/**
 * 后端 API 调用封装,统一携带 Cognito IdToken(§10 鉴权)。
 */
import { config } from "../config";
import { getIdToken } from "../auth/cognitoClient";

export class ApiError extends Error {
  status: number;
  detail: unknown;
  constructor(status: number, message: string, detail?: unknown) {
    super(message);
    this.status = status;
    this.detail = detail;
  }
}

async function request<T>(
  baseUrl: string,
  method: string,
  path: string,
  options: { body?: unknown; query?: Record<string, string | number | undefined> } = {}
): Promise<T> {
  const token = await getIdToken();
  if (!token) {
    throw new ApiError(401, "未登录或登录已过期,请重新登录");
  }

  const url = new URL(baseUrl.replace(/\/$/, "") + path);
  if (options.query) {
    for (const [key, value] of Object.entries(options.query)) {
      if (value !== undefined) url.searchParams.set(key, String(value));
    }
  }

  const resp = await fetch(url.toString(), {
    method,
    headers: {
      "Content-Type": "application/json",
      Authorization: token,
    },
    body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
  });

  const text = await resp.text();
  const data = text ? JSON.parse(text) : null;

  if (!resp.ok) {
    throw new ApiError(resp.status, (data && data.error) || `请求失败: ${resp.status}`, data);
  }
  return data as T;
}

/**
 * 创建一个绑定到指定 base URL 的 API 客户端实例,鉴权机制(Cognito IdToken)
 * 与错误处理均一致。用于支持 Kiro-Go 舰队管理器这类拥有独立 API Gateway
 * base URL 的子系统(见 config.ts 的 fleetApiBaseUrl),避免为每个子系统
 * 重复实现一遍 fetch 封装逻辑。
 */
export function createApiClient(baseUrl: string) {
  return {
    get: <T,>(path: string, query?: Record<string, string | number | undefined>) =>
      request<T>(baseUrl, "GET", path, { query }),
    post: <T,>(path: string, body?: unknown) => request<T>(baseUrl, "POST", path, { body }),
    put: <T,>(path: string, body?: unknown) => request<T>(baseUrl, "PUT", path, { body }),
    patch: <T,>(path: string, body?: unknown) => request<T>(baseUrl, "PATCH", path, { body }),
    delete: <T,>(path: string) => request<T>(baseUrl, "DELETE", path),
  };
}

export const apiClient = createApiClient(config.apiBaseUrl);
